import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useAddAsset } from "@/api/hooks/useProof";
import { ASSET_KIND_LABELS, AssetKindSchema, type AssetKind } from "@/api/types/asset";

const ASSET_KINDS = AssetKindSchema.options;

interface AddAssetDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

/**
 * Adds a media asset row (POST /v2/events/:id/assets). `r2_key` is captured
 * as a plain reference string here — mirrors ContractCard's `contract_key`
 * field on EventDetailPage — there's no file-upload widget yet (that's a
 * later phase; the contract's field is a string, not a multipart upload).
 */
export function AddAssetDialog({ open, onClose, eventId }: AddAssetDialogProps) {
  const [kind, setKind] = useState<AssetKind>("hero_photo");
  const [r2Key, setR2Key] = useState("");
  const [caption, setCaption] = useState("");

  const addAsset = useAddAsset();

  useEffect(() => {
    if (open) {
      setKind("hero_photo");
      setR2Key("");
      setCaption("");
    }
  }, [open]);

  const canSubmit = r2Key.trim().length > 0 && !addAsset.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await addAsset.mutateAsync({
        eventId,
        kind,
        r2_key: r2Key.trim(),
        caption: caption.trim() || undefined,
      });
      toastSuccess("Asset added");
      onClose();
    } catch (err) {
      toastError("Couldn't add asset", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Add asset">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Add asset</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect label="Kind" value={kind} onChange={(e) => setKind(e.target.value as AssetKind)}>
            {ASSET_KINDS.map((k) => (
              <option key={k} value={k}>
                {ASSET_KIND_LABELS[k]}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="Reference (R2 key)"
            required
            placeholder="e.g. events/2026-07-12/reveal.mp4"
            value={r2Key}
            onChange={(e) => setR2Key(e.target.value)}
            hint="Where the file lives in R2 — upload flow lands in a later phase."
          />

          <FormField
            label="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={addAsset.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {addAsset.isPending ? "Adding..." : "Add asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
